import { useState, useEffect, useMemo } from 'react';
import { 
  Users, Shield, Search, Mail, 
  Building, CheckCircle2, AlertCircle, Headphones, 
  Monitor, Globe, Settings, Layers, Send, MoreHorizontal,
  Copy, UserCheck, X, CheckSquare, Square, Sliders
} from 'lucide-react';
import { useAdminStore, AdminUserItem } from '../../../store/adminStore';
import { useAuthStore } from '../../../store/authStore';
import { Button } from '../../../components/Button';
import { Badge } from '../../../components/Badge';
import { Pagination } from '../../../components/Pagination';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '../../../components/DropdownMenu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../components/Tooltip';
import { AdminPageHeader, AdminEmptyState, AdminSkeletonTable } from '../components';
import { RoleChipsCell } from './RoleChipsCell';
import { UserUnifiedDrawer } from './UserUnifiedDrawer';
import toast from 'react-hot-toast';

export function UserManagement() {
  const { 
    users, stats, isLoading, 
    loadUsers, loadStats, inviteEmployee, toggleServiceStatus
  } = useAdminStore();
  const currentAuthUser = useAuthStore((s) => s.user);
  const isSuperAdmin = Boolean(
    currentAuthUser?.role === 'super_admin' ||
    currentAuthUser?.role === 'admin' ||
    String(currentAuthUser?.role || '').toLowerCase().includes('admin') ||
    (currentAuthUser?.roles || []).some((r: any) => {
      const code = String(r?.code || r?.name || r || '').toLowerCase();
      return code.includes('super_admin') || code.includes('admin') || code.includes('platform');
    })
  );

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDept, setSelectedDept] = useState('All');
  const [selectedService, setSelectedService] = useState('All');
  const [selectedRole, setSelectedRole] = useState('All');
  const [statusTab, setStatusTab] = useState<'all' | 'active' | 'pending' | 'no_account' | 'service'>('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(new Date());

  // Bulk Selection
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);

  // Unified Drawer
  const [drawerUser, setDrawerUser] = useState<AdminUserItem | null>(null);
  const [isInvitingId, setIsInvitingId] = useState<string | null>(null);

  useEffect(() => {
    loadStats();
    loadUsers();
  }, [loadStats, loadUsers]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([loadStats(), loadUsers()]);
      setLastRefreshed(new Date());
      toast.success('User directory refreshed');
    } catch {
      toast.error('Failed to refresh users');
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleInvite = async (user: AdminUserItem) => {
    const targetId = user.employee_id || user.id;
    setIsInvitingId(targetId);
    try {
      await inviteEmployee(targetId);
      toast.success(`Account invitation sent to ${user.email}`);
      await loadUsers();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to send invite');
    } finally {
      setIsInvitingId(null);
    }
  };

  const handleToggleServiceStatus = async (user: AdminUserItem) => {
    const targetUserId = user.user_id || user.id;
    if (!targetUserId) {
      toast.error('No valid user account found for this record');
      return;
    }
    const nextVal = !user.is_service_user;
    try {
      const res = await toggleServiceStatus(targetUserId, nextVal);
      toast.success(res.message);
      await loadUsers();
      await loadStats();
    } catch (err: any) {
      toast.error(err.response?.data?.message || err.message || 'Failed to update service status');
    }
  };

  // Unique departments from users
  const departments = useMemo(() => {
    const set = new Set<string>();
    const safeUsers = Array.isArray(users) ? users : [];
    safeUsers.forEach((u) => {
      if (u?.department) set.add(u.department);
    });
    return Array.from(set).sort();
  }, [users]);

  // Unique roles from users
  const availableRoles = useMemo(() => {
    const map = new Map<string, string>();
    const safeUsers = Array.isArray(users) ? users : [];
    safeUsers.forEach((u) => {
      const uRoles = Array.isArray(u?.roles) ? u.roles : [];
      uRoles.forEach((r) => {
        if (r?.code && r?.name) map.set(r.code, r.name);
      });
    });
    return Array.from(map.entries()).map(([code, name]) => ({ code, name }));
  }, [users]);

  // Filtering
  const filteredUsers = useMemo(() => {
    const safeUsers = Array.isArray(users) ? users : [];
    return safeUsers.filter((u) => {
      if (!u) return false;
      const searchLower = searchQuery.toLowerCase().trim();
      const matchSearch =
        !searchLower ||
        (u.email || '').toLowerCase().includes(searchLower) ||
        (u.first_name || '').toLowerCase().includes(searchLower) ||
        (u.last_name || '').toLowerCase().includes(searchLower) ||
        (u.employee_code || '').toLowerCase().includes(searchLower) ||
        (u.employee_id || '').toLowerCase().includes(searchLower);

      const matchDept = selectedDept === 'All' || u.department === selectedDept;

      let matchStatus = true;
      if (statusTab === 'active') matchStatus = u.status === 'active';
      else if (statusTab === 'pending') matchStatus = u.status === 'pending';
      else if (statusTab === 'no_account') matchStatus = u.has_account === false;
      else if (statusTab === 'service') matchStatus = u.is_service_user === true;

      let matchService = true;
      if (selectedService !== 'All') {
        const lowerSvc = selectedService.toLowerCase();
        const userServices = Object.keys(u.rolesByService || {});
        matchService = userServices.includes(lowerSvc);
      }

      let matchRole = true;
      if (selectedRole !== 'All') {
        const uRoles = Array.isArray(u.roles) ? u.roles : [];
        matchRole = uRoles.some((r) => r?.code === selectedRole);
      }

      return matchSearch && matchDept && matchStatus && matchService && matchRole;
    });
  }, [users, searchQuery, selectedDept, statusTab, selectedService, selectedRole]);

  // Pagination
  const totalPages = Math.ceil(filteredUsers.length / pageSize) || 1;
  const paginatedUsers = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredUsers.slice(start, start + pageSize);
  }, [filteredUsers, page, pageSize]);

  // Bulk Selection Handlers
  const handleSelectAll = () => {
    if (selectedUserIds.length === paginatedUsers.length) {
      setSelectedUserIds([]);
    } else {
      setSelectedUserIds(paginatedUsers.map((u) => u.id));
    }
  };

  const toggleSelectUser = (id: string) => {
    setSelectedUserIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleBulkInvite = async () => {
    const selectedUsers = users.filter((u) => selectedUserIds.includes(u.id));
    const inviteable = selectedUsers.filter((u) => u.has_account === false || u.status === 'pending');
    if (inviteable.length === 0) {
      toast.error('None of the selected users require account invitations');
      return;
    }
    toast.success(`Dispatching invitations to ${inviteable.length} users...`);
    for (const u of inviteable) {
      try {
        await inviteEmployee(u.employee_id || u.id);
      } catch {
        // continue
      }
    }
    toast.success(`Processed ${inviteable.length} invitations`);
    setSelectedUserIds([]);
    loadUsers();
  };

  return (
    <div className="min-h-full bg-background text-foreground p-4 space-y-4 pb-12">
      {/* Page Header */}
      <AdminPageHeader
        icon={Users}
        title="User & Identity Directory"
        subtitle="Unified user accounts, employee profiles, service delegations and role assignments"
        onRefresh={handleRefresh}
        isRefreshing={isRefreshing}
        lastRefreshed={lastRefreshed}
        badge={
          <Badge variant="outline" className="text-xs bg-primary/10 text-primary border-primary/20 font-medium">
            {(Array.isArray(users) ? users.length : 0)} Users
          </Badge>
        }
      />

      <div className="space-y-4">
        {/* Sticky Filters & Status Segmented Control */}
        <div className="sticky top-0 z-20 bg-background/95 backdrop-blur-sm border border-border rounded-lg p-4 shadow-xs space-y-3">
          {/* Status Segmented Control */}
          <div className="flex flex-wrap items-center justify-between gap-3 pb-3 border-b border-border/80">
            <div className="flex items-center gap-1 bg-secondary/60 p-1 rounded-lg border border-border text-xs">
              {[
                { key: 'all', label: 'All Users', count: Array.isArray(users) ? users.length : 0 },
                { key: 'active', label: 'Active', count: Array.isArray(users) ? users.filter((u) => u?.status === 'active').length : 0 },
                { key: 'pending', label: 'Pending', count: Array.isArray(users) ? users.filter((u) => u?.status === 'pending').length : 0 },
                { key: 'no_account', label: 'No Login', count: Array.isArray(users) ? users.filter((u) => u?.has_account === false).length : 0 },
                { key: 'service', label: 'External/Service', count: Array.isArray(users) ? users.filter((u) => u?.is_service_user).length : 0 },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => {
                    setStatusTab(tab.key as any);
                    setPage(1);
                  }}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all cursor-pointer ${
                    statusTab === tab.key
                      ? 'bg-card text-foreground shadow-xs font-semibold'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <span>{tab.label}</span>
                  <span className="ml-1.5 text-[10px] text-muted-foreground/80 font-normal">
                    ({tab.count})
                  </span>
                </button>
              ))}
            </div>

            <div className="text-xs text-muted-foreground">
              Showing <strong className="text-foreground">{filteredUsers.length}</strong> matching records
            </div>
          </div>

          {/* Search & Filter Dropdowns */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 pt-0.5">
            {/* Search */}
            <div className="relative">
              <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <input
                type="text"
                placeholder="Search by name, email, employee code..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(1);
                }}
                className="w-full pl-9 pr-3 py-2 text-xs rounded-lg border border-border bg-card text-foreground focus:outline-none focus:ring-1 focus:ring-primary shadow-2xs"
              />
            </div>

            {/* Department */}
            <div>
              <select
                value={selectedDept}
                onChange={(e) => {
                  setSelectedDept(e.target.value);
                  setPage(1);
                }}
                className="w-full px-3 py-2 text-xs rounded-lg border border-border bg-card text-foreground focus:outline-none focus:ring-1 focus:ring-primary cursor-pointer shadow-2xs"
              >
                <option value="All">All Departments</option>
                {departments.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </div>

            {/* Platform Service */}
            <div>
              <select
                value={selectedService}
                onChange={(e) => {
                  setSelectedService(e.target.value);
                  setPage(1);
                }}
                className="w-full px-3 py-2 text-xs rounded-lg border border-border bg-card text-foreground focus:outline-none focus:ring-1 focus:ring-primary cursor-pointer shadow-2xs"
              >
                <option value="All">All Platform Services</option>
                <option value="support">Support Service</option>
                <option value="asset">Asset Management</option>
                <option value="admin">Admin Platform</option>
              </select>
            </div>

            {/* Role Filter */}
            <div>
              <select
                value={selectedRole}
                onChange={(e) => {
                  setSelectedRole(e.target.value);
                  setPage(1);
                }}
                className="w-full px-3 py-2 text-xs rounded-lg border border-border bg-card text-foreground focus:outline-none focus:ring-1 focus:ring-primary cursor-pointer shadow-2xs"
              >
                <option value="All">All Roles</option>
                {availableRoles.map((r) => (
                  <option key={r.code} value={r.code}>
                    {r.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Floating Bulk Action Bar */}
        {selectedUserIds.length > 0 && (
          <div className="flex items-center justify-between p-3 px-4 bg-card border border-primary/40 rounded-lg shadow-md animate-in slide-in-from-top-2 text-xs">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-primary">{selectedUserIds.length}</span>
              <span className="text-foreground">users selected</span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="primary"
                size="sm"
                onClick={handleBulkInvite}
                className="h-7 text-xs"
              >
                <Send className="size-3 mr-1.5" />
                <span>Invite Selected</span>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedUserIds([])}
                className="h-7 text-xs text-muted-foreground hover:text-foreground"
              >
                <X className="size-3.5 mr-1" />
                <span>Clear Selection</span>
              </Button>
            </div>
          </div>
        )}

        {/* Data Table */}
        <div className="bg-card border border-border rounded-lg overflow-hidden shadow-xs">
          {isLoading && users.length === 0 ? (
            <AdminSkeletonTable rows={8} cols={5} />
          ) : paginatedUsers.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-secondary/40 border-b border-border text-muted-foreground font-semibold uppercase text-[10px] tracking-wider">
                  <tr>
                    <th className="py-3 px-4 w-10 text-center">
                      <input
                        type="checkbox"
                        checked={selectedUserIds.length === paginatedUsers.length && paginatedUsers.length > 0}
                        onChange={handleSelectAll}
                        className="rounded border-border text-primary focus:ring-primary cursor-pointer"
                        title="Select all on this page"
                      />
                    </th>
                    <th className="py-3 px-4">User</th>
                    <th className="py-3 px-4">Department &amp; Code</th>
                    <th className="py-3 px-4">Account Status</th>
                    <th className="py-3 px-4">Assigned Roles</th>
                    <th className="py-3 px-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {paginatedUsers.map((u, rowIdx) => {
                    const rowKey = u.id || u.user_id || u.employee_id || u.email || `user-row-${rowIdx}`;
                    const fullName =
                      `${u.first_name || ''} ${u.last_name || ''}`.trim() || (u.email ? u.email.split('@')[0] : 'User');
                    const isSelected = Boolean(u.id && selectedUserIds.includes(u.id));
                    const avatarLetter = ((u.first_name?.[0] || u.email?.[0] || 'U') as string).toUpperCase();

                    return (
                      <tr
                        key={rowKey}
                        className={`hover:bg-secondary/20 transition-colors group ${
                          isSelected ? 'bg-primary/5' : ''
                        }`}
                      >
                        {/* Checkbox */}
                        <td className="py-3 px-4 text-center">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => u.id && toggleSelectUser(u.id)}
                            disabled={!u.id}
                            className="rounded border-border text-primary focus:ring-primary cursor-pointer disabled:opacity-40"
                          />
                        </td>

                        {/* User Info with Avatar & External/Service Badge */}
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <div className="size-8 rounded-full bg-primary/10 border border-primary/20 text-primary font-bold flex items-center justify-center text-xs shrink-0">
                              {avatarLetter}
                            </div>
                            <div className="min-w-0">
                              <div className="font-semibold text-foreground flex items-center gap-1.5 flex-wrap">
                                <span
                                  onClick={() => setDrawerUser(u)}
                                  className="cursor-pointer hover:text-primary transition-colors"
                                >
                                  {fullName}
                                </span>
                                {u.is_service_user && (
                                  <span
                                    className="inline-flex cursor-help"
                                    title="Service or external integration account created without a standard employee profile"
                                  >
                                    <Badge
                                      variant="outline"
                                      className="text-[9px] py-0 px-1 bg-info/10 text-info border-info/30"
                                    >
                                      External/Service
                                    </Badge>
                                  </span>
                                )}
                              </div>
                              <span className="text-[11px] text-muted-foreground font-mono truncate block">
                                {u.email || '—'}
                              </span>
                            </div>
                          </div>
                        </td>

                        {/* Department & Employee ID / Code */}
                        <td className="py-3 px-4">
                          <div className="space-y-0.5">
                            <div className="font-medium text-foreground">{u.department || '—'}</div>
                            <div className="text-[11px] text-muted-foreground">
                              {u.designation || 'Staff'}{' '}
                              {u.employee_code
                                ? `· ${u.employee_code}`
                                : u.employee_id
                                ? `· ${String(u.employee_id).slice(0, 8)}`
                                : ''}
                            </div>
                          </div>
                        </td>

                        {/* Account Status */}
                        <td className="py-3 px-4">
                          {u.has_account === false ? (
                            <Badge
                              variant="outline"
                              className="text-[10px] bg-warning/10 text-warning border-warning/30 flex items-center gap-1 w-fit"
                            >
                              <AlertCircle className="size-3" />
                              <span>No Login Account</span>
                            </Badge>
                          ) : u.status === 'active' ? (
                            <Badge
                              variant="outline"
                              className="text-[10px] bg-success/10 text-success border-success/30 flex items-center gap-1 w-fit"
                            >
                              <span className="size-1.5 rounded-full bg-success" />
                              <span>Active</span>
                            </Badge>
                          ) : (
                            <Badge
                              variant="outline"
                              className="text-[10px] bg-secondary text-muted-foreground border-border flex items-center gap-1 w-fit"
                            >
                              <span className="size-1.5 rounded-full bg-muted-foreground" />
                              <span className="capitalize">{u.status || 'Pending'}</span>
                            </Badge>
                          )}
                        </td>

                        {/* Assigned Roles (Max 2 chips + overflow popover) */}
                        <td className="py-3 px-4 max-w-xs">
                          <RoleChipsCell roles={Array.isArray(u.roles) ? u.roles : []} />
                        </td>

                        {/* Actions (Merged Single Action + Row Menu) */}
                        <td className="py-3 px-4 text-right whitespace-nowrap">
                          <div className="flex items-center justify-end gap-1.5">
                            {/* Single primary row action */}
                            {u.has_account === false ? (
                              <Button
                                variant="primary"
                                size="sm"
                                disabled={isInvitingId === (u.employee_id || u.id)}
                                onClick={() => handleInvite(u)}
                                className="text-xs h-7 px-2.5"
                              >
                                <Send className="size-3 mr-1" />
                                <span>{isInvitingId === (u.employee_id || u.id) ? 'Inviting...' : 'Invite'}</span>
                              </Button>
                            ) : (
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => setDrawerUser(u)}
                                className="text-xs h-7 px-2.5"
                              >
                                <Shield className="size-3 mr-1 text-primary" />
                                <span>Manage &amp; Details</span>
                              </Button>
                            )}

                            {/* Row Action Menu */}
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                                >
                                  <MoreHorizontal className="size-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-48">
                                <DropdownMenuItem
                                  onClick={() => setDrawerUser(u)}
                                  className="text-xs cursor-pointer"
                                >
                                  <Shield className="size-3.5 mr-2 text-primary" />
                                  <span>View Roles &amp; Permissions</span>
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => {
                                    navigator.clipboard.writeText(u.id);
                                    toast.success('User ID copied to clipboard');
                                  }}
                                  className="text-xs cursor-pointer"
                                >
                                  <Copy className="size-3.5 mr-2" />
                                  <span>Copy User ID</span>
                                </DropdownMenuItem>
                                {u.email && (
                                  <DropdownMenuItem
                                    onClick={() => {
                                      navigator.clipboard.writeText(u.email);
                                      toast.success('Email copied to clipboard');
                                    }}
                                    className="text-xs cursor-pointer"
                                  >
                                    <Mail className="size-3.5 mr-2" />
                                    <span>Copy Email</span>
                                  </DropdownMenuItem>
                                )}
                                {(u.has_account === false || u.status === 'pending') && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      onClick={() => handleInvite(u)}
                                      className="text-xs cursor-pointer text-primary"
                                    >
                                      <Send className="size-3.5 mr-2" />
                                      <span>Send Account Invitation</span>
                                    </DropdownMenuItem>
                                  </>
                                )}
                                {isSuperAdmin && (u.id || u.user_id) && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      onClick={() => handleToggleServiceStatus(u)}
                                      className="text-xs cursor-pointer text-foreground"
                                    >
                                      <Sliders className="size-3.5 mr-2 text-primary" />
                                      <span>{u.is_service_user ? 'Unmark as External/Service' : 'Mark as External/Service'}</span>
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <AdminEmptyState
              title="No users match your criteria"
              description="Try adjusting your search terms, status filter or department selection."
              icon={Users}
              action={{
                label: 'Clear Filters',
                onClick: () => {
                  setSearchQuery('');
                  setSelectedDept('All');
                  setSelectedService('All');
                  setSelectedRole('All');
                  setStatusTab('all');
                },
                variant: 'secondary',
              }}
            />
          )}

          {/* Pagination */}
          {filteredUsers.length > pageSize && (
            <div className="p-3 border-t border-border flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                Page {page} of {totalPages} ({filteredUsers.length} total)
              </span>
              <Pagination
                page={page}
                pageCount={totalPages}
                onPageChange={setPage}
              />
            </div>
          )}
        </div>
      </div>

      {/* Unified Right-Side Details Drawer */}
      {drawerUser && (
        <UserUnifiedDrawer
          user={drawerUser}
          isOpen={Boolean(drawerUser)}
          onClose={() => setDrawerUser(null)}
          onSuccess={() => {
            loadUsers();
            loadStats();
          }}
        />
      )}
    </div>
  );
}
